import React, { useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import UploadIcon from "@mui/icons-material/Upload";
import DownloadIcon from "@mui/icons-material/Download";
import GitIcon from "@mui/icons-material/GitHub";
import ApiIcon from "@mui/icons-material/ApiOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import PlayIcon from "@mui/icons-material/PlayArrow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import useActions, { useAppSelector } from "../../app/hooks";
import { CloudUpload } from "@mui/icons-material";

import CreateDashForm from "./CreateDashForm";
import CreateFolderForm from "./CreateFolderForm";
import CreateRoleForm from "./CreateRoleForm";
import CreateFileForm from "./CreateFileForm";
import CreateRepoFolderForm from "./CreateRepoFolderForm";
import CreateScenarioForm from "./CreateScenarioForm";
import AttachUserForm from "./AttachUserForm";
import AttachRepoForm from "./AttachRepoForm";
import { selectEditingDash } from "../editor/dashEditorSlice";
import { selectFolderName, selectRepoId, selectReposMode, selectRoleId, selectRolesMode, selectScenarioId, selectScenariosMode } from "../library/librarySlice";

import {
  Workspace,
} from "../../types/User";

// Automation related
import AutomationList from "./automations/AutomationList";
import HostList from "./automations/HostList";
import FactotumsList from "./automations/FactotumsList";
import ProjectsList from "./automations/ProjectsList";
import HotTokensList from "./automations/HotTokensList";
import RunAutomationDialog from "./automations/RunAutomationDialog";
import AutomationOutputModal from "./automations/AutomationOutputModal";
import GitOutputModal from "./GitOutputModal";

/**
 * The create menu.
 *
 * @export
 * @param {{user: User | null, documentTemplates: DocumentTemplate[]}} props
 * @return {*}
 */
export function CreateMenu(props: {
  // user: User | null;
  // documentTemplates: DocumentTemplate[];
  workspaces: Workspace[] | undefined;
  workspaceId: number | undefined;
}) {
  const actions = useActions();
  const selectedDocumentTemplate = useAppSelector(selectEditingDash);
  const rolesMode = useAppSelector(selectRolesMode);
  const reposMode = useAppSelector(selectReposMode);
  const scenariosMode = useAppSelector(selectScenariosMode);
  const viewRoleId = useAppSelector(selectRoleId);
  const viewRepoId = useAppSelector(selectRepoId);
  const viewScenarioId = useAppSelector(selectScenarioId);
  const viewRepoPath = useAppSelector(selectFolderName);

  //console.log("A", viewRepoPath);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorGitEl, setAnchorGitEl] = React.useState<null | HTMLElement>(null);
  const [anchorAutomationEl, setAnchorAutomationEl] = React.useState<null | HTMLElement>(null);

  const [manageAutomationList, setManageAutomationList] = React.useState<boolean>(false);
  const [manageHostList, setManageHostList] = React.useState<boolean>(false);
  const [manageFactotumList, setManageFactotumList] = React.useState<boolean>(false);
  const [manageProjectList, setManageProjectList] = React.useState<boolean>(false);
  const [manageHotTokenList, setManageHotTokenList] = React.useState<boolean>(false);
  const [runAutomationDialog, setRunAutomationDialog] = React.useState<boolean>(false);

  // Create menu details
  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Git menu details
  const handleOpenGitMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorGitEl(event.currentTarget);
  };

  const handleCloseGitMenu = () => {
    setAnchorGitEl(null);
  };

  // Automations menu details
  const handleOpenAutomationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorAutomationEl(event.currentTarget);
  };

  const handleCloseAutomationMenu = () => {
    setAnchorAutomationEl(null);
  };

  const handleCommit = () => {
    setAnchorGitEl(null);
    const message = window.prompt("Commit message?");
    if (message !== null && message !== undefined && message != "") {
      actions.commitRepo({repoId: viewRepoId, message: message});
    }
  }

  const handlePush = () => {
    setAnchorGitEl(null);
    actions.pushRepo(viewRepoId);
  }

  const handlePull = () => {
    setAnchorGitEl(null);
    actions.pullRepo(viewRepoId);
  }

  const handleRunAutomation = () => {
    setRunAutomationDialog(true);
    setAnchorAutomationEl(null);
  }

  const handleManageAutomations = () => {
    setManageAutomationList(true);
    setAnchorAutomationEl(null);
  }

  const handleManageHosts = () => {
    setManageHostList(true);
    setAnchorAutomationEl(null);
  }

  const handleManageFactotums = () => {
    setManageFactotumList(true);
    setAnchorAutomationEl(null);
  }

  const handleManageProjects = () => {
    setManageProjectList(true);
    setAnchorAutomationEl(null);
  }

  const handleManageHotTokens = () => {
    setManageHotTokenList(true);
    setAnchorAutomationEl(null);
  }


  const [createWorkbookFormOpen, setCreateWorkbookFormOpen] = React.useState<{ open: boolean}>({ open: false });
  const [createFolderFormOpen, setCreateFolderFormOpen] = React.useState<{ open: boolean}>({ open: false });
  const [createRoleFormOpen, setCreateRoleFormOpen] = React.useState<{ open: boolean}>({ open: false});
  const [createFileOpen, setCreateFileOpen] = React.useState<{ open: boolean}>({ open: false});
  const [createRepoFolderOpen, setCreateRepoFolderOpen] = React.useState<{ open: boolean}>({ open: false});
  const [attachUserFormOpen, setAttachUserFormOpen] = React.useState<{ open: boolean}>({ open: false});
  const [attachRepoFormOpen, setAttachRepoFormOpen] = React.useState<{ open: boolean}>({ open: false});
  const [createScenarioFormOpen, setCreateScenarioFormOpen] = React.useState<{ open: boolean}>({ open: false});
  const [importTemplateFormOpen, setImportTemplateFormOpen] =
    React.useState(false);
  const [createDocumentFormOpen, setCreateDocumentFormOpen] = React.useState(false);

  const handleClickCreateDocument = () => {
    setAnchorEl(null);
    setCreateDocumentFormOpen(true)
  };

  const handleClickCreateAdhocDocument = () => {
    setAnchorEl(null);
    // if (props.user?.default_organization_id !== org.id)
    //   actions.setOrganization(org);
  };

  const handleClickImportTemplate = () => {
    setAnchorEl(null);
    setImportTemplateFormOpen(true);
    // if (props.user?.default_organization_id !== org.id)
    //   actions.setOrganization(org);
  };

  const handleClickImportAdhocDocument = () => {
    setAnchorEl(null);
    // if (props.user?.default_organization_id !== org.id)
    //   actions.setOrganization(org);
  };

  return (
    <Box sx={{ flexGrow: 0 }}>
      <Tooltip title="Create Templates and Documents">
        <Button
          startIcon={<AddIcon />}
          sx={{ color: "white" }}
          onClick={handleOpenMenu}
        >
          Create
        </Button>
      </Tooltip>
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        { !rolesMode && !reposMode && !scenariosMode && viewRoleId < 0 && <MenuItem key="createWorkbook" onClick={() => setCreateWorkbookFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create Workbook</ListItemText>
        </MenuItem> }
        { !rolesMode && !reposMode && !scenariosMode && viewRoleId < 0 && <MenuItem key="createFolder" onClick={() => setCreateFolderFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create Folder</ListItemText>
        </MenuItem> }
        { rolesMode && viewRoleId < 0 && <MenuItem key="createRole" onClick={() => setCreateRoleFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create Role</ListItemText>
        </MenuItem> }
        { rolesMode && viewRoleId > 0 && <MenuItem key="attachUser" onClick={() => setAttachUserFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Attach User to Role</ListItemText>
        </MenuItem> }
        { reposMode && viewRepoId < 0 && <MenuItem key="attachRepo" onClick={() => setAttachRepoFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Attach a Code Repository</ListItemText>
        </MenuItem> }
        { scenariosMode && viewScenarioId < 0 && <MenuItem key="createScenario" onClick={() => setCreateScenarioFormOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create Scenario</ListItemText>
        </MenuItem> }
        { reposMode && viewRepoId > 0 && <MenuItem key="createFile" onClick={() => setCreateFileOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create a New File</ListItemText>
        </MenuItem> }
        { reposMode && viewRepoId > 0 && <MenuItem key="createRepoFolder" onClick={() => setCreateRepoFolderOpen({open: true})}>
          <ListItemIcon>
            <AddIcon></AddIcon>
          </ListItemIcon>
          <ListItemText>Create a New Folder</ListItemText>
        </MenuItem> }
       
      </Menu>





        { reposMode && viewRepoId > 0 && <Tooltip title="Git actions">
        <Button
          startIcon={<GitIcon />}
          sx={{ color: "white" }}
          onClick={handleOpenGitMenu}
        >
          Git
        </Button>
      </Tooltip> }
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar-git"
        anchorEl={anchorGitEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorGitEl)}
        onClose={handleCloseGitMenu}
      >
        <MenuItem key="commitGit" onClick={() => handleCommit() }>
          <ListItemIcon>
            <CheckIcon></CheckIcon>
          </ListItemIcon>
          <ListItemText>Commit changes...</ListItemText>
        </MenuItem>

        <MenuItem key="pushGit" onClick={() => handlePush() }>
          <ListItemIcon>
            <UploadIcon></UploadIcon>
          </ListItemIcon>
          <ListItemText>Push changes...</ListItemText>
        </MenuItem>

        <MenuItem key="pullGit" onClick={() => handlePull() }>
          <ListItemIcon>
            <DownloadIcon></DownloadIcon>
          </ListItemIcon>
          <ListItemText>Pull changes...</ListItemText>
        </MenuItem>
       
      </Menu>



      { <Tooltip title="Automations">
        <Button
          startIcon={<ApiIcon />}
          sx={{ color: "white" }}
          onClick={handleOpenAutomationMenu}
        >
          Automations
        </Button>
      </Tooltip> }
      <Menu
        sx={{ mt: "45px" }}
        id="menu-appbar-auto"
        anchorEl={anchorAutomationEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorAutomationEl)}
        onClose={handleCloseAutomationMenu}
      >
        {reposMode && viewRepoId > 0 && <MenuItem key="runAutomation" onClick={() => handleRunAutomation() }>
          <ListItemIcon>
            <PlayIcon></PlayIcon>
          </ListItemIcon>
          <ListItemText>Run automation...</ListItemText>
        </MenuItem> }

        {reposMode && viewRepoId > 0 && <MenuItem key="manageAutomations" onClick={() => handleManageAutomations() }>
          <ListItemIcon>
            <SettingsIcon></SettingsIcon>
          </ListItemIcon>
          <ListItemText>Manage automations...</ListItemText>
        </MenuItem>}

        <MenuItem key="manageHosts" onClick={() => handleManageHosts() }>
          <ListItemIcon>
            <SettingsIcon></SettingsIcon>
          </ListItemIcon>
          <ListItemText>Manage hosts...</ListItemText>
        </MenuItem>

        <MenuItem key="manageFactotums" onClick={() => handleManageFactotums() }>
          <ListItemIcon>
            <SettingsIcon></SettingsIcon>
          </ListItemIcon>
          <ListItemText>Manage factotums...</ListItemText>
        </MenuItem>

        <MenuItem key="manageProjects" onClick={() => handleManageProjects() }>
          <ListItemIcon>
            <SettingsIcon></SettingsIcon>
          </ListItemIcon>
          <ListItemText>Manage projects...</ListItemText>
        </MenuItem>

        {props.workspaceId !== undefined && (
          <MenuItem key="manageHotTokens" onClick={() => handleManageHotTokens() }>
            <ListItemIcon>
              <SettingsIcon></SettingsIcon>
            </ListItemIcon>
            <ListItemText>Manage hot tokens...</ListItemText>
          </MenuItem>
        )}

      </Menu>



      <CreateDashForm
        open={createWorkbookFormOpen.open}
        parentCloseHandle={() => {
          setCreateWorkbookFormOpen({open: false});
          setAnchorEl(null);
        }}
      />
      <CreateFolderForm
        open={createFolderFormOpen.open}
        parentCloseHandle={() => {
          setCreateFolderFormOpen({open: false});
          setAnchorEl(null);
        }}
      />
      <CreateRoleForm
        open={createRoleFormOpen.open}
        parentCloseHandle={() => {
          setCreateRoleFormOpen({open: false});
          setAnchorEl(null);
        }}
      />
      <CreateFileForm
        open={createFileOpen.open}
        repoId={viewRepoId}
        repoPath={viewRepoPath}
        parentCloseHandle={() => {
          setCreateFileOpen({open: false});
          setAnchorEl(null);
        }}
      />
      <CreateRepoFolderForm
        open={createRepoFolderOpen.open}
        repoId={viewRepoId}
        repoPath={viewRepoPath}
        parentCloseHandle={() => {
          setCreateRepoFolderOpen({open: false});
          setAnchorEl(null);
        }}
      />
      { props.workspaces !== undefined && props.workspaceId !== undefined && <AttachUserForm
        open={attachUserFormOpen.open}
        workspaces={props.workspaces}
        workspaceId={props.workspaceId}
        roleId={viewRoleId}
        parentCloseHandle={() => {
          setAttachUserFormOpen({open: false});
          setAnchorEl(null);
        }}
      /> }

      { props.workspaces !== undefined && props.workspaceId !== undefined && <AttachRepoForm
        open={attachRepoFormOpen.open}
        parentCloseHandle={() => {
          setAttachRepoFormOpen({open: false});
          setAnchorEl(null);
        }}
      /> }

      <CreateScenarioForm
        open={createScenarioFormOpen.open}
        parentCloseHandle={() => {
          setCreateScenarioFormOpen({open: false});
          setAnchorEl(null);
        }}
      />

      { manageAutomationList && <AutomationList
        parentCloseHandle={() => {
          setManageAutomationList(false);
          setAnchorEl(null);
        }}
        runOrManage={'manage'}
      /> }

      { manageHostList && <HostList
        parentCloseHandle={() => {
          setManageHostList(false);
          setAnchorEl(null);
        }}
      /> }

      { manageFactotumList && <FactotumsList
        parentCloseHandle={() => {
          setManageFactotumList(false);
          setAnchorEl(null);
        }}
      /> }

      { manageProjectList && <ProjectsList
        parentCloseHandle={() => {
          setManageProjectList(false);
          setAnchorEl(null);
        }}
      /> }

      { manageHotTokenList && props.workspaceId !== undefined && (
        <HotTokensList
          workspaceId={props.workspaceId}
          parentCloseHandle={() => {
            setManageHotTokenList(false);
            setAnchorAutomationEl(null);
          }}
        />
      )}

      { runAutomationDialog && <RunAutomationDialog
        parentCloseHandle={() => {
          setRunAutomationDialog(false);
          setAnchorEl(null);
        }}
      /> }

      <AutomationOutputModal />
      <GitOutputModal />

    </Box>
  );
}
